import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import {
  getPredictionOutcomeEventID,
  IconName,
  useCallbackRef,
  type FilterableSelectProperty,
  type IPredictionOutcomeEvent,
  type IPredictionOutcomeEventSeriesRecord,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import type { PriceGridSourceItem } from '../../types';
import type { PredictionMarketsPriceGridFilterState } from './types';

const EMPTY_SELECTIONS: string[] = [];
const getOptionLabel = (option: string) => option;

export function matchesPredictionMarketsPriceGridFilters(
  event: IPredictionOutcomeEvent,
  filterState: PredictionMarketsPriceGridFilterState,
  selectedSeriesIDs: Set<string> | undefined
) {
  const categories = filterState.categories ?? EMPTY_SELECTIONS;

  return (
    (categories.length === 0 || categories.includes(event.Category)) &&
    (selectedSeriesIDs == null || (event.SeriesID != null && selectedSeriesIDs.has(event.SeriesID)))
  );
}

export function matchesPredictionMarketsPriceGridSourceItemFilters({
  sourceItem,
  eventsByID,
  filterState,
  selectedSeriesIDs,
}: {
  sourceItem: PriceGridSourceItem;
  eventsByID: Map<string, IPredictionOutcomeEvent>;
  filterState: PredictionMarketsPriceGridFilterState;
  selectedSeriesIDs: Set<string> | undefined;
}) {
  const eventID = getPredictionOutcomeEventID(sourceItem.security);
  const event = eventID ? eventsByID.get(eventID) : undefined;
  if (!event || !matchesPredictionMarketsPriceGridFilters(event, filterState, selectedSeriesIDs)) {
    return false;
  }

  const search = filterState.search?.trim().toLowerCase();
  if (!search) {
    return true;
  }

  const security = sourceItem.security;
  return [event.EventName, event.EventSymbol, event.EventID, security.DisplaySymbol, security.Symbol].some(value =>
    value?.toLowerCase().includes(search)
  );
}

export function getSelectedPredictionMarketSeriesIDs(
  eventSeries: IPredictionOutcomeEventSeriesRecord[],
  selectedSeriesNames: string[] | undefined
): Set<string> | undefined {
  if (!selectedSeriesNames || selectedSeriesNames.length === 0) {
    return undefined;
  }

  const selectedSeriesNameSet = new Set(selectedSeriesNames);
  const selectedSeriesIDs = new Set<string>();
  for (const series of eventSeries) {
    if (selectedSeriesNameSet.has(series.SeriesName)) {
      selectedSeriesIDs.add(series.SeriesID);
    }
  }
  return selectedSeriesIDs;
}

export function usePredictionMarketsPriceGridFilterControls({
  filterState,
  onFilterStateChanged,
}: {
  filterState: PredictionMarketsPriceGridFilterState;
  onFilterStateChanged: (filterState: PredictionMarketsPriceGridFilterState) => void;
}) {
  const { events, eventSeries } = usePredictionsContext();
  const categories = filterState.categories ?? EMPTY_SELECTIONS;
  const seriesNames = filterState.seriesNames ?? EMPTY_SELECTIONS;

  const categoryOptions = useMemo(() => [...new Set(events.map(event => event.Category))], [events]);
  const seriesOptions = useMemo(() => [...new Set(eventSeries.map(series => series.SeriesName))], [eventSeries]);

  const categoryProperty = useMemo(
    (): FilterableSelectProperty<'categories', string> => ({
      key: 'categories',
      label: 'Category',
      icon: IconName.CubeTransparent,
      options: categoryOptions,
      getOptionLabel,
      dropdownOptions: { dropdownWidth: 220 },
    }),
    [categoryOptions]
  );

  const seriesProperty = useMemo(
    (): FilterableSelectProperty<'seriesNames', string> => ({
      key: 'seriesNames',
      label: 'Series',
      icon: IconName.CubeTransparent,
      options: seriesOptions,
      getOptionLabel,
      dropdownOptions: { dropdownWidth: 260 },
    }),
    [seriesOptions]
  );

  const updateSelections = useCallbackRef((key: 'categories' | 'seriesNames', selections: string[]) => {
    const uniqueSelections = [...new Set(selections)];
    onFilterStateChanged({
      ...filterState,
      [key]: uniqueSelections.length === 0 ? undefined : uniqueSelections,
    });
  });

  const setCategories = useCallback(
    (selections: string[]) => updateSelections('categories', selections),
    [updateSelections]
  );
  const setSeriesNames = useCallback(
    (selections: string[]) => updateSelections('seriesNames', selections),
    [updateSelections]
  );

  return {
    categories: {
      property: categoryProperty,
      selections: categories,
      onSelectionsChange: setCategories,
    },
    series: {
      property: seriesProperty,
      selections: seriesNames,
      onSelectionsChange: setSeriesNames,
    },
  };
}
